import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ref, onValue, off } from 'firebase/database';
import { database } from '../config/firebase';
import './ArticleDetail.css';
import { setMeta, setCanonical, setHreflang, setHtmlLang, stripHtml, SITE_NAME } from '../utils/seo';

interface Article {
  title: string;
  content: string;
  description?: string;
  author: string;
  date: string;
}


const ArticleDetail: React.FC = () => {
  const { i18n, t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [article, setArticle] = useState<Article | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!id) {
      setError(t('technicalArticles.error'));
      setLoading(false);
      return;
    }

    const currentLang = i18n.language === 'ru' ? 'ru' : i18n.language === 'ar' ? 'ar' : 'en';
    const articleRef = ref(database, `technicalArticles/${currentLang}/${id}`);

    onValue(
      articleRef,
      (snapshot) => {
        const data = snapshot.val();
        if (data) {
          setArticle(data);
        } else {
          setError(t('technicalArticles.noArticles'));
        }
        setLoading(false);
      },
      (err) => {
        setError(t('technicalArticles.loadError'));
        setLoading(false);
        console.error('Error fetching article:', err);
      }
    );

    return () => {
      off(articleRef);
    };
  }, [id, i18n.language, t]);

  // --- SEO: per-article title, description, Open Graph & canonical ---
  useEffect(() => {
    if (!article) return;
    const baseTitle = document.title;
    const desc = (article.description || stripHtml(article.content)).slice(0, 160);
    const url = typeof window !== 'undefined' ? window.location.href : '';

    document.title = `${article.title} | ${SITE_NAME}`;
    setHtmlLang(i18n.language);
    setHreflang(window.location.pathname);
    setMeta('name', 'description', desc);
    setMeta('property', 'og:title', article.title);
    setMeta('property', 'og:description', desc);
    setMeta('property', 'og:type', 'article');
    setMeta('property', 'og:site_name', SITE_NAME);
    if (url) {
      setMeta('property', 'og:url', url);
      setCanonical(url);
    }
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', article.title);
    setMeta('name', 'twitter:description', desc);

    // JSON-LD structured data
    const ld = document.createElement('script');
    ld.type = 'application/ld+json';
    ld.id = 'article-jsonld';
    ld.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: desc,
      author: { '@type': 'Organization', name: article.author || 'Nish Global' },
      publisher: { '@type': 'Organization', name: SITE_NAME },
      datePublished: article.date,
      inLanguage: i18n.language,
    });
    document.getElementById('article-jsonld')?.remove();
    document.head.appendChild(ld);

    return () => {
      document.title = baseTitle;
      document.getElementById('article-jsonld')?.remove();
    };
  }, [article, i18n.language]);

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      let locale = 'en-US';
      if (i18n.language === 'ru') locale = 'ru-RU';
      else if (i18n.language === 'ar') locale = 'ar-SA';
      return date.toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' });
    } catch {
      return dateString;
    }
  };

  const readingMinutes = (content: string) => {
    const words = stripHtml(content).split(' ').filter(Boolean).length;
    return Math.max(1, Math.round(words / 200));
  };

  if (loading) {
    return (
      <div className="article-detail-page">
        <div className="article-detail-container">
          <div className="loading">{t('technicalArticles.loading')}</div>
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="article-detail-page">
        <div className="article-detail-container">
          <div className="error-message">
            <h2>{error || t('technicalArticles.noArticles')}</h2>
            <button onClick={() => navigate('/technical-articles')}>← {t('menu.technicalArticles')}</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="article-detail-page">
      {/* Hero header */}
      <header className="article-hero">
        <div className="article-hero-inner">
          <nav className="article-crumb">
            <Link to="/technical-articles">{t('menu.technicalArticles')}</Link>
            <span>/</span>
            <span className="current">{article.title}</span>
          </nav>
          <span className="article-cat">{t('technicalArticles.category')}</span>
          <h1>{article.title}</h1>
          {article.description && <p className="article-lead">{article.description}</p>}
          <div className="article-meta-row">
            <span className="article-meta-author">
              <span className="article-avatar">N</span>
              {article.author || 'Nish Global'}
            </span>
            <span className="article-dot">·</span>
            <span>{formatDate(article.date)}</span>
            <span className="article-dot">·</span>
            <span>{readingMinutes(article.content)} min read</span>
          </div>
        </div>
      </header>

      <div className="article-detail-container">
        <button className="back-button" onClick={() => navigate('/technical-articles')}>
          ← {t('menu.technicalArticles')}
        </button>

        <article className="article-detail">
          <div className="article-content" dangerouslySetInnerHTML={{ __html: article.content }} />
        </article>

        {/* CTA */}
        <aside className="article-cta">
          <h3>{t('common.contact', { defaultValue: 'Contact Us' })}</h3>
          <p>{t('technicalArticles.intro')}</p>
          <Link to="/contact" className="article-cta-btn">{t('common.getQuote')}</Link>
        </aside>
      </div>
    </div>
  );
};

export default ArticleDetail;
